import { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import arrowRight from "../assets/images/bet-frenzy/arrowRight.svg";
import arrowLeft from "../assets/images/bet-frenzy/arrowLeft.svg";
import { getDreamCasinoGames } from "../redux/slices/games/casino/dreamCasinoGameListSlice";

const SCROLL_AMOUNT = 250;
const DESKTOP_BREAKPOINT = 1024;

export default function CasinoCarousel({ setIsPopupOpen, setSelectedGame }) {
  const carouselRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: profileData } = useSelector((state) => state.getProfile);

  const [games, setGames] = useState([]);
  const [count, setCount] = useState(0);
  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const updateArrowState = useCallback(() => {
    const container = carouselRef.current;
    if (!container) return;

    const scrollLeft = container.scrollLeft;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    setIsLeftDisabled(scrollLeft <= 5);
    setIsRightDisabled(scrollLeft >= maxScrollLeft - 5);
  }, []);

  const scroll = useCallback((direction) => {
    if (!carouselRef.current) return;
    carouselRef.current.scrollBy({
      left: direction == "left" ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: "smooth",
    });
  }, []);

  const getData = useCallback(() => {
    setIsLoading(true);
    dispatch(
      getDreamCasinoGames({
        page: 1,
        limit: 20,
        perpage: 20,
        search_by_category: "non-live",
        search_by_provider: "",
        search_by_text: "",
        is_popular: 0,
        parent_user_id: profileData?.user?.parent_user_id,
      })
    )
      .unwrap()
      .then((res) => {
        setCount(res.totalCount);

        const validatedGames = res?.data?.map((game) => ({
          ...game,
          isBroken: false,
        }));

        validatedGames?.forEach((game, idx) => {
          const img = new Image();
          img.src = game?.url_thumb;

          img.onload = () => {
            setGames((prev) => {
              const copy = [...prev];
              copy[idx] = { ...game, isBroken: false };
              return copy;
            });
          };

          img.onerror = () => {
            setGames((prev) => {
              const copy = [...prev];
              copy[idx] = { ...game, isBroken: true };
              return copy;
            });
          };
        });
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Failed :", error);
      });
  }, [dispatch]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    const container = carouselRef.current;
    if (!container) return;

    container.addEventListener("scroll", updateArrowState);
    updateArrowState();

    return () => container.removeEventListener("scroll", updateArrowState);
  }, [games, updateArrowState]);

  return (
    <div className="carousel-container">
      <div className="carousel-header d-flex flex-column flex-lg-row align-items-start">
        <h4 className="fw-medium hero-heading text-capitalize color-CCD6F6 mt-3 mb-2 fs-20">
          Casino
        </h4>

        <div className="mt-lg-3 mb-2 d-flex w-100 justify-content-between justify-content-lg-end">
          <span
            className="see-all align-content-center cursor-pointer"
            onClick={() =>
              navigate(`/casino-games/casino`, {
                state: { category: "", name: "casino", sidebar_cat: "casino" },
              })
            }
          >
            See all <span className="ms-2 text-gray">({count})</span>
          </span>

          <div className="d-flex">
            <span
              className={`nav-btn-ft mx-1 left d-flex justify-content-center align-items-center ${
                isLeftDisabled ? "disabled-arrow" : ""
              }`}
              onClick={() => !isLeftDisabled && scroll("left")}
            >
              <img
                src={arrowLeft}
                width={15}
                alt="Left"
                style={{ filter: isLeftDisabled ? "grayscale(100%)" : "none" }}
                loading="lazy"
              />
            </span>

            <span
              className={`nav-btn-ft mx-1 right d-flex justify-content-center align-items-center ${
                isRightDisabled ? "disabled-arrow" : ""
              }`}
              onClick={() => !isRightDisabled && scroll("right")}
            >
              <img
                src={arrowRight}
                width={15}
                alt="Right"
                style={{ filter: isRightDisabled ? "grayscale(100%)" : "none" }}
                loading="lazy"
              />
            </span>
          </div>
        </div>
      </div>

      <div className="carousel-wrapper justify-content-center">
        <div
          className="carousel-wrap casino-home-wrap"
          ref={carouselRef}
          style={{
            gridTemplateRows:
              window.innerWidth > DESKTOP_BREAKPOINT && games.length > 4
                ? "repeat(2, 1fr)"
                : "repeat(1, 1fr)",
          }}
        >
          {isLoading ? (
            <SkeletonTheme baseColor="#2a2d3e" highlightColor="#3e4259">
              {[...Array(8)].map((_, i) => (
                <Skeleton
                  key={i}
                  width={225}
                  height={220}
                  style={{
                    borderRadius: "20px",
                    margin: "5px",
                  }}
                />
              ))}
            </SkeletonTheme>
          ) : games?.length > 0 ? (
            games.map((game, index) => (
              <img
                className="game-card-casino align-content-center cursor-pointer"
                key={index}
                onClick={() => {
                  if (profileData) {
                    setSelectedGame(game);
                    setIsPopupOpen(true);
                    // navigate("/game-launch", {
                    //   state: { game_id: game?._id, game: game },
                    // });
                  } else {
                    navigate("/");
                    sessionStorage.setItem("fromLogin", "true");
                  }
                }}
                draggable="false"
                src={game?.url_thumb}
                alt={game?.game_name}
                loading="lazy"
                style={{
                  objectFit: "fill",
                  border: game?.isBroken ? "1px solid #3a3f5c" : "none",
                  borderRadius: game?.isBroken ? "20px" : "none",
                  display: game?.isBroken ? "block" : "",
                  alignContent: game?.isBroken ? "center" : "",
                  background: game?.isBroken ? "rgba(245, 247, 250, 0.12)" : "",
                }}
              />
            ))
          ) : (
            <span className="d-flex  text-white">No Data Found</span>
          )}
        </div>
      </div>
    </div>
  );
}
